import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useEffect, useState } from 'react';
import { PrimaryButton, SecondaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { observer } from 'mobx-react';
import { Outlet, useLocation } from 'react-router-dom';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { IMFPStore } from '../../../modules/stores';
import { AdvertiserForm } from '../../../modules/components/forms/advertiser/advertiser.form';
import { SiderSlider } from '@monorepo/base/src/components/sider-slider/sider-slider';
import styles from './create-advertiser.module.scss';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import { getUrlLevelUp } from '@monorepo/tools/src/lib/utils/url';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { Helmet } from 'react-helmet-async';

export const CreateAdvertiser = observer(() => {
	const { advertiserStore, accountStore } = useStores<IMFPStore>();
	const { dispatchLog } = useLogs();
	const [isFormClicked, setFormClicked] = useState<boolean>(false);
	const advertiserCrudStore = advertiserStore.getCrud();
	const accountCrudStore = accountStore.getCrud();
	const advertiser = advertiserCrudStore.getData();

	const navigate = useNavigateParams();
	const location = useLocation();
	const closeSiderSlider = (isBlockRequest = false) => {
		navigate(getUrlLevelUp({ pathname: location.pathname }), { state: { isBlockRequest } as BetweenPagesStateProps });
	};

	useEffect(() => {
		return () => {
			advertiserCrudStore.reset();
			accountCrudStore.reset();
		};
	}, [location]);

	const name = advertiser.getName();
	const formStore = advertiserCrudStore.getFormStore();
	const isValidForm = formStore.getIsValid();
	const isLoading = advertiserCrudStore.getIsLoading();
	const isSuccess = advertiserCrudStore.getIsSuccess();
	const error = advertiserCrudStore.getHttpError();

	useEffect(() => {
		if (!isValidForm && isFormClicked) {
			dispatchLog({
				msg: 'Please complete all mandatory fields',
				type: DispatchLogsTypes.Error,
			});
		}
	}, [isFormClicked]);

	useEffect(() => {
		if (isSuccess) {
			closeSiderSlider();
			dispatchLog({
				msg: `Advertiser ${name} was successfully added`,
				type: DispatchLogsTypes.Success,
			});
			advertiserCrudStore.setIsSuccess(false);
		}
	}, [isSuccess]);

	const onCreateAdvertiser = () => {
		setFormClicked(true);
		advertiserStore.getCrud().create();
	};
	return (
		<SiderSlider onClose={() => closeSiderSlider(true)} isOpen={true}>
			<Helmet>
				<title>Create Advertiser</title>
			</Helmet>
			<Disable is={isLoading}>
				<Page unstyled className={styles.wrapper}>
					<Pagebar>
						<PagebarTitle classes={{ title: styles.title }} unstyled>
							Create new Advertiser
						</PagebarTitle>
					</Pagebar>
					<AdvertiserForm />
					<BottomBar classes={{ barClassses: styles.advertiserBottomBar, wrapperClassses: styles.advertiserBottomBarWrapper }}>
						{error ? <ErrorMsg error={error} classes={{ error: styles.errorMsg }} /> : null}
						<BottomBarActions>
							<SecondaryButton
								onClick={() => closeSiderSlider(true)}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'cancel_advertiser_button')] }}>
								Cancel
							</SecondaryButton>
							<PrimaryButton
								onClick={() => onCreateAdvertiser()}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'save_advertiser_button')] }}>
								Save
							</PrimaryButton>
						</BottomBarActions>
					</BottomBar>
				</Page>
			</Disable>
			<Outlet />
		</SiderSlider>
	);
});
